import dynamic from "next/dynamic";
import Image from "next/image";
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import Link from "next/link";

const HeroContactFormClient = dynamic(
  () => import("../SecondaryHero/HeroContactFormClient"),
  { ssr: false }
);

interface SecondaryHeroData {
  title?: string;
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  button_desktop?: {
    label?: string;
    href?: string;
  };
  button_mobile?: {
    label?: string;
    href?: string;
  };
  images?: {
    images_id: {
      alt: string;
      image_file: {
        image: {
          id: string;
          filename_download: string;
        };
      };
    };
  }[];
}

export const fields = [
  "title",

  "heading.text",
  "heading.title",

  "subheading.text",
  "subheading.title",

  "button_desktop.label",
  "button_desktop.href",
  "button_mobile.label",
  "button_mobile.href",

  "images.images_id.alt",
  "images.images_id.image_file.image.id",
  "images.images_id.image_file.image.filename_download",
] as const;

interface SecondaryHeroProps {
  data?: SecondaryHeroData;
}

export default function SecondaryHero({ data }: SecondaryHeroProps) {
  const title = data?.heading?.title || "";
  const subtitle = data?.subheading?.text || data?.subheading?.title || "";
  const mobileButtonText = data?.button_mobile?.label || "";
  const desktopButtonText = data?.button_desktop?.label || "";
  const buttonHref =
    data?.button_desktop?.href || data?.button_mobile?.href || "#";
  const mobileImage = data?.images?.[0];
  const desktopImage = data?.images?.[1];
  const mobileImageUrl =
    mobileImage?.images_id?.image_file?.image?.id &&
    mobileImage?.images_id?.image_file?.image?.filename_download
      ? getReadableImageUrl(
          mobileImage.images_id.image_file.image.id,
          mobileImage.images_id.image_file.image.filename_download
        )
      : "/images/secondary-hero-mobile.png";
  const desktopImageUrl =
    desktopImage?.images_id?.image_file?.image?.id &&
    desktopImage?.images_id?.image_file?.image?.filename_download
      ? getReadableImageUrl(
          desktopImage.images_id.image_file.image.id,
          desktopImage.images_id.image_file.image.filename_download
        )
      : "/images/secondary-hero.png";
  const mobileImageAlt = mobileImage?.images_id?.alt || "Hero Image Mobile";
  const desktopImageAlt = desktopImage?.images_id?.alt || "Hero Image Desktop";

  return (
    <section className="relative h-auto w-full overflow-hidden bg-white lg:h-[803px]">
      <HeroContactFormClient />
      <div className="relative lg:pt-0">
        <Image
          src={mobileImageUrl}
          alt={mobileImageAlt}
          width={578}
          height={786}
          className="h-[786px] w-full object-cover lg:hidden"
          sizes="(max-width: 1024px) 100vw, 0vw"
          priority
          fetchPriority="high"
          quality={65}
        />
        <Image
          src={desktopImageUrl}
          alt={desktopImageAlt}
          width={1322}
          height={674}
          className="hidden lg:block lg:absolute lg:left-0 lg:top-0 lg:h-[803px] lg:w-full object-cover object-left"
          sizes="(max-width: 1024px) 0vw, 100vw"
          loading="lazy"
          quality={65}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary opacity-70 lg:hidden" />
      </div>
      <div className="container relative mx-auto">
        <div className="absolute bottom-0 w-full px-8 lg:bg-white/65 lg:rounded-xl p-6 lg:bottom-auto lg:right-16 lg:top-48 lg:max-w-[709px] lg:p-10">
          <div className="mb-8 flex flex-col gap-7 lg:mb-16 lg:gap-12">
            <h1 className="text-[2.5rem] font-essonnes font-light leading-tight md:text-[3.625rem] lg:text-[4rem] text-white lg:text-primary">
              {title}
            </h1>
            <div
              className="text-[1rem] font-normal leading-normal xl:text-xl font-greycliff tracking-tight text-white lg:text-primary lg:font-light lg:leading-7"
              dangerouslySetInnerHTML={sanitizeHTML(subtitle)}
            />
          </div>
          {desktopButtonText && mobileButtonText && (
            <Link
              href={buttonHref}
              className="mb-8 inline-flex h-14 w-full items-center justify-center rounded-[50px] bg-light lg:mb-0 lg:h-[70px] lg:w-[333px] lg:bg-primary"
            >
              <span className="font-greycliff text-lg font-normal leading-normal tracking-tight text-primary lg:text-xl lg:text-white">
                <span className="lg:hidden">{mobileButtonText}</span>
                <span className="hidden lg:inline">{desktopButtonText}</span>
              </span>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
